import { useEffect, useRef } from "react";
import { useBilling } from "@/lib/hooks/use-billing";
import { useCreditPurchase, netCreditsAvailable } from "@/lib/hooks/use-credit-purchase";
import { usePaymentMethodStatus } from "@/lib/hooks/use-payment-method-status";
import { isLovablePreview } from "@/lib/utils/environment";
import { useGetWorkspacePreferencesQuery } from "@/lib/graphql/types-and-hooks";

/**
 * Next-interaction auto-reload (billing spec: offline overage → opt-in
 * auto-load of a credit pack).
 *
 * When a workspace returns already in overage from offline/agent activity and
 * has opted into auto-reload *with a card on file*, buy the configured pack
 * once so the user isn't interrupted. Renders nothing.
 *
 * Scope: this is only the opted-in branch. The not-opted-in hard gate ("must
 * buy before they can proceed") is a separate component/PR, and in-app
 * overages keep their existing immediate buy-prompt at the action sites.
 *
 * Idempotent: guarded per workspace + billing period via sessionStorage, so a
 * pack that doesn't fully cover the overage can't trigger repeat charges on
 * reload; a successful purchase also clears the overage condition via the
 * normalized cache. Requires a card on file — otherwise purchaseCreditPack
 * would redirect to Stripe Checkout, which must never happen unprompted.
 */
export default function OverageAutoReload() {
  const preview = isLovablePreview();
  const { billingInfo, loading, workspaceId } = useBilling();
  const { purchaseCreditPack } = useCreditPurchase();
  const firedRef = useRef(false);

  const limit = billingInfo?.totalCreditsLimit ?? 0;
  // Net across all sources (a fully-consumed pack nets 0, not an overage).
  const inOverage = !preview && !loading && limit > 0 && netCreditsAvailable(billingInfo) < 0;
  const period = billingInfo?.billingPeriod ?? "";

  // Always-mounted: only reach for prefs / payment-status when actually in
  // overage (rare), so the common case adds no network on every app load.
  const { data: prefsData } = useGetWorkspacePreferencesQuery({
    variables: { workspaceId: workspaceId ?? "" },
    skip: preview || !workspaceId || !inOverage,
  });
  const autoRecharge = prefsData?.workspacePreferences?.autoRechargeEnabled ?? false;
  const packId = prefsData?.workspacePreferences?.autoRechargePackId ?? null;

  const { hasValidCard } = usePaymentMethodStatus({ skip: !inOverage || !autoRecharge });

  useEffect(() => {
    if (!inOverage || !autoRecharge || !packId || !workspaceId || !hasValidCard) return;
    if (firedRef.current) return;

    const key = `billing.autoReload.${workspaceId}.${period}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, "1");
    } catch {
      // sessionStorage unavailable — the in-mount ref still prevents repeats.
    }
    firedRef.current = true;

    purchaseCreditPack(packId).catch(() => {
      // Swallow: the persistent low-credit banner (and the next-interaction
      // gate, once added) cover a failed/declined auto-reload.
    });
  }, [inOverage, autoRecharge, packId, workspaceId, hasValidCard, period, purchaseCreditPack]);

  return null;
}
